/**
 * Quem pode o que.
 *
 * As regras vivem aqui, puras e sem banco, por um motivo: autorizacao espalhada
 * por rotas e o jeito mais confiavel de esquecer uma. Com as decisoes num lugar
 * so, o teste cobre a REGRA, e a rota so precisa lembrar de perguntar.
 *
 * O erro que este modulo existe para impedir nao e "operador consegue editar" —
 * esse alguem nota. E o silencioso: uma rota nova que ninguem lembrou de
 * proteger, ou uma consulta que aceita `workspaceId` da URL sem conferir de
 * quem ele e. Por isso `podeAcessarWorkspace` recebe a lista de workspaces da
 * organizacao e nao um booleano ja resolvido: quem chama nao tem como
 * "esquecer" de resolver.
 */

export type Papel = "admin" | "operador";

export const PAPEIS: readonly Papel[] = ["admin", "operador"] as const;

export function ehPapelValido(valor: unknown): valor is Papel {
  return typeof valor === "string" && (PAPEIS as readonly string[]).includes(valor);
}

/**
 * Identidade resolvida de quem esta fazendo o pedido, dentro de UMA
 * organizacao. A mesma pessoa pode ter papeis diferentes em organizacoes
 * diferentes — um integrador e `admin` na propria planta e `operador` na do
 * cliente —, entao o papel nunca viaja sozinho: ele vem sempre colado a
 * organizacao em que vale.
 */
export interface Ator {
  userId: string;
  organizationId: string;
  papel: Papel;
}

/**
 * Escrever = mudar o que TODO MUNDO ve.
 *
 * Cobre o que e COLETIVO: a identidade dos sinais, que descreve o que a maquina
 * mede e vale para todo mundo que olha. Nomear uma grandeza errado engana o
 * turno inteiro, entao continua sendo ato de administrador.
 *
 * NAO cobre mais o layout do painel. Ate 28/08/2026 cobria, e a razao era boa
 * enquanto durou: o painel era um so, compartilhado, e sem essa trava quem
 * estava apenas acompanhando o turno podia reconfigurar o alarme de uma prensa
 * sem querer. Desde que cada pessoa tem o proprio painel
 * (`dashboard_layout_settings.user_id`), a trava perdeu o alvo — arrumar a
 * propria tela nao alcanca ninguem. Ver `podeEditarProprioPainel`.
 */
export function podeEditar(papel: Papel): boolean {
  return papel === "admin";
}

/**
 * Mexer no PROPRIO painel: mover card, redimensionar, criar grafico, escolher
 * o tipo de widget.
 *
 * Todo membro pode. O painel tem dono desde a migration 008, e o dono e sempre
 * quem esta pedindo — a rota nunca aceita um `userId` do cliente. Entao o pior
 * que um operador consegue fazer aqui e baguncar a propria tela, e conviver com
 * ela e problema dele.
 *
 * Recusar isso teria um custo real e nenhum ganho: o operador herda o painel de
 * quem o cadastrou e ficaria preso a ele, sem poder nem aumentar um grafico que
 * nao cabe na tela dele.
 */
export function podeEditarProprioPainel(_papel: Papel): boolean {
  return true;
}

/** Convidar pessoa, trocar papel, redefinir senha. */
export function podeGerenciarPessoas(papel: Papel): boolean {
  return papel === "admin";
}

/** Adotar gateway, remover device, mexer em credencial de broker. */
export function podeGerenciarDispositivos(papel: Papel): boolean {
  return papel === "admin";
}

/**
 * Criar e remover SETOR (workspace) dentro da organizacao.
 *
 * Separado de `podeGerenciarDispositivos` porque as duas coisas tem alcance
 * diferente: adotar um gateway mexe num equipamento, criar um setor mexe na
 * ESTRUTURA que todo mundo navega. Fundir as duas faria a regra do setor
 * herdar, sem ninguem decidir, qualquer afrouxamento futuro na regra do
 * dispositivo.
 */
export function podeGerenciarWorkspaces(papel: Papel): boolean {
  return papel === "admin";
}

/** Ler o painel, o historico e o estado dos sinais. */
export function podeLer(_papel: Papel): boolean {
  // Todo membro le. Se um dia existir papel de convidado com escopo parcial,
  // e AQUI que ele entra — nao numa condicao espalhada pelas rotas.
  return true;
}

/**
 * A organizacao do ator e dona deste workspace?
 *
 * Recebe a lista de workspaces da organizacao em vez de um booleano ja
 * decidido: assim quem chama nao consegue passar `true` por engano, e o teste
 * cobre a comparacao de verdade.
 */
export function podeAcessarWorkspace(
  workspaceId: string,
  workspacesDaOrganizacao: readonly string[],
): boolean {
  if (typeof workspaceId !== "string" || workspaceId.length === 0) return false;
  return workspacesDaOrganizacao.includes(workspaceId);
}

export type Operacao =
  | "ler"
  | "editar"
  | "editarProprioPainel"
  | "gerenciarPessoas"
  | "gerenciarDispositivos"
  | "gerenciarWorkspaces";

/**
 * Decisao unica: este ator pode fazer esta operacao neste workspace?
 *
 * Devolve um motivo em vez de so `false` porque a API precisa distinguir 403
 * ("voce e operador") de 404 ("este workspace nao e da sua organizacao"). E a
 * distincao importa: responder 403 para um workspace de OUTRA empresa
 * confirmaria que ele existe.
 */
export type Veredito =
  | { permitido: true }
  | { permitido: false; motivo: "fora-da-organizacao" | "papel-insuficiente" };

export function autorizar(
  ator: Ator,
  operacao: Operacao,
  workspaceId: string | null,
  workspacesDaOrganizacao: readonly string[],
): Veredito {
  if (workspaceId !== null && !podeAcessarWorkspace(workspaceId, workspacesDaOrganizacao)) {
    // Vem PRIMEIRO de proposito. Se o papel fosse conferido antes, um operador
    // e um admin receberiam respostas diferentes para um workspace alheio — e
    // a diferenca ja contaria que ele existe.
    return { permitido: false, motivo: "fora-da-organizacao" };
  }

  const permitido =
    operacao === "ler"
      ? podeLer(ator.papel)
      : operacao === "editarProprioPainel"
        ? podeEditarProprioPainel(ator.papel)
        : operacao === "editar"
          ? podeEditar(ator.papel)
          : operacao === "gerenciarPessoas"
            ? podeGerenciarPessoas(ator.papel)
            : operacao === "gerenciarWorkspaces"
              ? podeGerenciarWorkspaces(ator.papel)
              : podeGerenciarDispositivos(ator.papel);

  return permitido ? { permitido: true } : { permitido: false, motivo: "papel-insuficiente" };
}
